import React, { Component, Context, ContextType } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema, StrictRJSFSchema, TemplatesType, UiSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import type { JSONSchema7Definition } from 'json-schema';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { LegacyContext } from '~/stormpath/components/LegacyContextType';
import { AuthContext } from '~/stormpath/components/Router';

import { Alert, Box } from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';
import {
  REGIONS_COUNTRY_CODES,
  isPhoneNumberValidForRegion,
} from '@luxuryescapes/lib-regions/lib/phoneNumberValidation';

import { ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import UsersService from '~/services/UsersService';

import GridObjectFieldTemplate from './Forms/GridObjectFieldTemplate';
import SimpleArrayFieldItemTemplate from './Forms/SimpleArrayFieldItemTemplate';
import SimpleArrayFieldTemplate from './Forms/SimpleArrayFieldTemplate';

const defaultFieldsOrder = [
  'customer_support_code',
  'title',
  'givenName',
  'surname',
  'dob',
  'email',
  'email_verified',
  'phone_prefix',
  'phone',
  'shadow_ban_user',
  'country_code',
  'postcode',
  'recently_used_airport_code',
  'resetPassword',
  'password',
  '*',
];

const DEFAULT_PHONE_FORMAT_HELP_MESSAGE = 'local number only, eg 402123123. No leading 0, no +61, no prefixes.';

const initUiSchema: UiSchema = {
  'ui:options': { gridLayout: true },
  'ui:order': defaultFieldsOrder,
  // Row 1
  customer_support_code: { 'ui:colSpan': 1, 'ui:readonly': true },
  title: { 'ui:widget': 'select', 'ui:colSpan': 1 },
  // Row 2
  dob: { 'ui:widget': 'date' },
  email: { 'ui:widget': 'email' },
  email_verified: { 'ui:readonly': true },
  // Row 3
  phone: {
    'ui:help': DEFAULT_PHONE_FORMAT_HELP_MESSAGE,
  },
  // Location fields
  country_code: { 'ui:colSpan': 1 },
  postcode: { 'ui:colSpan': 1 },
  recently_used_airport_code: { 'ui:emptyValue': null, 'ui:colSpan': 1 },
  resetPassword: { 'ui:colSpan': 1 },
  roles: { 'ui:colSpan': 3 },
  vendors: { 'ui:colSpan': 3 },
  partnerships: {
    'ui:colSpan': 6,
    // Enable grid layout on each individual partnership so their fields are 1/3 width
    kfp: { 'ui:options': { gridLayout: true } },
    vff: { 'ui:options': { gridLayout: true } },
  },
};

const FORM_TEMPLATES: Partial<TemplatesType> = {
  ObjectFieldTemplate: GridObjectFieldTemplate,
  ArrayFieldTemplate: SimpleArrayFieldTemplate,
  ArrayFieldItemTemplate: SimpleArrayFieldItemTemplate,
};

const PHONE_FORMAT_HELP_MESSAGE_BY_REGION = {
  AU: 'AU local number only, e.g. 412345678. No leading 0, no +61, no prefixes.',
  GB: 'GB local number only, e.g. 7123456789. No leading 0, no +44, no prefixes.',
  US: 'US local number only, e.g. 4151234567. No leading 0, no +1, no prefixes.',
  NZ: 'NZ local number only, e.g. 212345678. No leading 0, no +64, no prefixes.',
  SG: 'Singapore local number only, eg. 8021231234. No leading 0, no +65, no prefixes.',
};

type Props = {
  user: App.User;
  schema: RJSFSchema;
  brand: App.Brands;
  update?: (user: App.User) => void;
};

type FormData = Omit<App.User, 'gdpr'> & {
  resetPassword?: boolean;
  password?: string;
  vendors?: Array<string>;
  gdpr: boolean | string;
};

type State = {
  formData: FormData;
  schema: RJSFSchema;
  alertSuccess: boolean;
  alertError: boolean;
  errorMessage: string;
  partnershipsAlertError: boolean;
  partnershipsErrorMessage: string;
  flightEnabled: boolean;
  referralProgramEnabled: boolean;
  shadowBanUser: boolean;
};

export default class UpdateUser extends Component<Props, State> {
  static contextType: Context<LegacyContext> = AuthContext;
  context: ContextType<typeof AuthContext>;

  submitButton: HTMLButtonElement;

  constructor(props) {
    super(props);

    const formData: FormData = cloneDeep(this.props.user);
    const schema = cloneDeep(this.props.schema);

    const preferredRegions = ['AU', 'NZ', 'US', 'GB', 'SG'];
    const sortedRegionCodes = [
      ...preferredRegions,
      ...Object.keys(REGIONS_COUNTRY_CODES)
        .filter((code) => !preferredRegions.includes(code))
        .sort((a, b) => REGIONS_COUNTRY_CODES[a].countryName.localeCompare(REGIONS_COUNTRY_CODES[b].countryName)),
    ];

    const phonePrefixSchemas: Array<JSONSchema7Definition> = sortedRegionCodes.map((regionCode) => {
      const region = REGIONS_COUNTRY_CODES[regionCode];
      const internationalPhonePrefix = region.countryCode;
      const regionName = region.countryName;

      return {
        type: 'string',
        title: `${regionName} - ${regionCode} (+${internationalPhonePrefix})`,
        enum: [
          // what is this and why?
          // CA and US have the same international phone prefix of +1
          // if we give them the same value here, every time US is selected in the dropdown field for
          // phone prefix, it will select CA instead in the dropdown, which is confusing for the user
          //
          // so applying this transformation ensures that we can distinguish between CA and US, and that
          // the correct option is selected in the dropdown
          region === 'CA' || region.code === 'US'
            ? `${internationalPhonePrefix}_${regionCode}`
            : internationalPhonePrefix,
        ],

        // When CA or US is selected, we only want to capture the international phone prefix
        transform: (value) => value.split('_')[0],
      };
    });

    schema.properties.phone_prefix = {
      ...(schema.properties.phone_prefix as object),
      oneOf: phonePrefixSchemas,
    };

    // Set a default international phone prefix if not found
    // It might not be found initially if the phone prefix is a pre-existing invalid phone prefix
    const phonePrefixRegion = Object.keys(REGIONS_COUNTRY_CODES).find(
      (regionCode) => REGIONS_COUNTRY_CODES[regionCode].countryCode === formData.phone_prefix,
    );
    if (formData.phone_prefix !== '1_US' && formData.phone_prefix !== '1_CA' && !phonePrefixRegion) {
      formData.phone_prefix = '61';
    }

    this.state = {
      schema,
      formData,
      alertSuccess: false,
      alertError: !formData.country_code,
      errorMessage: !formData.country_code ? 'Country is a required field!' : '',
      partnershipsAlertError: false,
      partnershipsErrorMessage: '',
      flightEnabled: formData.flights_enabled,
      referralProgramEnabled: formData.referral_program_enabled,
      shadowBanUser: formData.shadow_ban_user,
    };

    this.customValidate = this.customValidate.bind(this);
  }

  customValidate(formData, errors, uiSchema) {
    const phonePrefix = formData.phone_prefix.split('_')[0];
    const regionCode = Object.keys(REGIONS_COUNTRY_CODES).find(
      (regionCode) => REGIONS_COUNTRY_CODES[regionCode].countryCode === phonePrefix,
    );

    const isPhoneNumberValid = isPhoneNumberValidForRegion({
      phoneNumber: formData.phone,
      regionCode,
    });

    if (!isPhoneNumberValid) {
      errors.phone.addError('Phone number is not valid for the selected region/phone prefix');
    }

    return errors;
  }

  getRegionCodeFromPhonePrefix(phonePrefix: string) {
    const phonePrefixParts = phonePrefix.split('_');
    let selectedRegionCode;

    if (phonePrefixParts.length > 1) {
      // e.g. in the format 1_US or 1_CA, we want to get the 'US' or 'CA'
      selectedRegionCode = phonePrefixParts[1];
    } else {
      const selectedRegion = Object.keys(REGIONS_COUNTRY_CODES).find(
        (regionCode) => REGIONS_COUNTRY_CODES[regionCode].countryCode === phonePrefixParts[0],
      );

      selectedRegionCode = selectedRegion;
    }

    return selectedRegionCode;
  }

  handleStateData = () => {
    const schema = { ...this.state.schema };
    const formData = { ...this.state.formData };
    if (this.context.user.roles.includes(ROLE_ICS_STORE_TEAM)) {
      delete schema.properties.shadow_ban_user;
    }

    const rolesEnumNames = (schema.properties.roles as StrictRJSFSchema)?.items?.['enumNames'];

    //reset password
    schema.properties = {
      ...schema.properties,
      title: {
        type: 'string',
        title: 'Title',
        enum: ['Mr', 'Ms', 'Mrs', 'Miss', 'Master'],
        default: 'Mr',
      },
      email_verified: { type: 'boolean', title: 'Email Verified' },
      resetPassword: {
        type: 'boolean',
        title: 'Reset Password',
      },
      customer_support_code: {
        type: 'string',
        title: 'Customer Support Code',
      },
      roles: {
        ...(schema.properties.roles as object),
        items: {
          ...((schema.properties.roles as StrictRJSFSchema).items as object),
          title: 'Role',
        },
      },
      recently_used_airport_code: {
        ...(schema.properties.recently_used_airport_code as object),
        type: ['string', 'null'],
      },
      postcode: {
        ...(schema.properties.postcode as object),
        type: ['string', 'null'],
      },
      phone_prefix: {
        ...(schema.properties.phone_prefix as object),
        type: ['string', 'null'],
      },
    };

    const uiSchema: UiSchema = {
      ...initUiSchema,
      roles: {
        ...(initUiSchema.roles as object),
        items: {
          'ui:enumNames': rolesEnumNames,
        },
      },
    };

    delete schema.properties.password;

    // reset password
    if (formData.resetPassword) {
      schema.properties.password = { type: 'string', title: 'Password' };
      uiSchema['ui:order'] = defaultFieldsOrder;
    } else {
      delete formData.password;
    }

    //partnerships
    if (window.configs.KRIS_FLYER_ENABLED !== 'true') {
      delete (schema.properties.partnerships as StrictRJSFSchema).properties.kfp;
    }

    if (window.configs.QANTAS_EARN_ORDER_BASED_ENABLED === 'true') {
      delete (schema.properties.partnerships as StrictRJSFSchema).properties.qff;
    }

    //vendor
    if (!formData.roles.includes('vendor-user')) {
      schema.properties = { ...schema.properties };
      if (formData.vendors) {
        delete formData.vendors;
      }
      if (schema.properties.vendors) {
        delete schema.properties.vendors;
      }
    } else {
      schema.properties = {
        ...schema.properties,
        vendors: {
          type: 'array',
          title: 'Vendor ID',
          items: { type: 'string' },
        },
      };

      // Preserve existing uiSchema settings (including grid layout)
    }

    if (formData.roles.length === 0 && formData.status === 'ENABLED') {
      formData.roles = ['customer'];
    }

    if (formData.gdpr) {
      formData.gdpr = true;
    } else {
      delete formData.gdpr;
    }

    if (!formData.phone) {
      delete formData.phone;
    }

    if (!formData.country_code) {
      delete formData.country_code;
    }

    if (!formData.dob) {
      delete formData.dob;
    }

    if (this.context.user.roles.includes(ROLE_ICS_STORE_TEAM)) {
      uiSchema.dob = { ...uiSchema.dob, 'ui:widget': 'hidden' };
    }

    uiSchema.banned_led_user = { 'ui:widget': 'hidden' };

    const selectedRegionCode = this.getRegionCodeFromPhonePrefix(formData.phone_prefix);
    const phoneNumberHelpMessage =
      PHONE_FORMAT_HELP_MESSAGE_BY_REGION[selectedRegionCode] || DEFAULT_PHONE_FORMAT_HELP_MESSAGE;
    uiSchema.phone = {
      ...uiSchema.phone,
      'ui:help': phoneNumberHelpMessage,
    };

    return { schema, formData, uiSchema };
  };

  handleChange = (data: IChangeEvent<FormData>) => {
    const { formData } = data;

    this.setState({
      formData,
      alertSuccess: false,
      alertError: false,
      errorMessage: '',
      partnershipsAlertError: false,
      partnershipsErrorMessage: '',
      flightEnabled: formData.flights_enabled,
      referralProgramEnabled: formData.referral_program_enabled,
      shadowBanUser: formData.shadow_ban_user,
    });
  };

  handleSubmit = async (event: IChangeEvent<FormData>) => {
    const { formData } = event;
    delete formData.flights_enabled;
    delete formData.referral_program_enabled;
    delete formData.shadow_ban_user;
    const shouldUpdateShadowBanUser = typeof this.state.shadowBanUser === 'boolean';

    if (!formData.dob) {
      formData.dob = null;
    }

    if (formData.phone) {
      // replace all non-numeric characters with an empty string
      formData.phone = formData.phone.replace(/[^0-9]/g, '');
    }

    let user = formData as App.User;
    let userSaved = false;
    try {
      user = await UsersService.updateUser(formData, this.props.brand);

      // todo: check if we can toggle all flags in parallel
      //       potential can lead to lock
      const toggleFlight = await UsersService.toggle({
        id_member: formData.id_member,
        toggle_name: 'flights_enabled',
        toggle_value: this.state.flightEnabled,
      });

      const toggleReferralProgram = await UsersService.toggle({
        id_member: formData.id_member,
        toggle_name: 'referral_program_enabled',
        toggle_value: this.state.referralProgramEnabled,
      });

      let toggleShadowBanUser;
      if (shouldUpdateShadowBanUser) {
        toggleShadowBanUser = await UsersService.toggle({
          id_member: formData.id_member,
          toggle_name: 'shadow_ban_user',
          toggle_value: this.state.shadowBanUser,
        });
      }
      if (
        user &&
        toggleFlight &&
        toggleReferralProgram &&
        (!shouldUpdateShadowBanUser || (shouldUpdateShadowBanUser && toggleShadowBanUser))
      ) {
        userSaved = true;
      }
    } catch (err) {
      const errorMessage = err.errors && err.errors.length ? err.errors[0] : err.message;
      this.setState({
        alertSuccess: false,
        alertError: true,
        errorMessage: errorMessage,
      });
    }

    if (userSaved && !isEqual(formData.partnerships, this.props.user.partnerships)) {
      try {
        await UsersService.updatePartnershipsDetails(formData.id_member, formData.partnerships);
      } catch (err) {
        const errorMessage = err.errors && err.errors.length ? err.errors[0] : err.message;
        this.setState({
          partnershipsAlertError: true,
          partnershipsErrorMessage: errorMessage,
        });
      }
    }

    if (userSaved) {
      user = await UsersService.getUser(formData.id_member, {
        brand: this.props.brand,
      });

      this.setState(
        {
          formData: cloneDeep(user),
          alertSuccess: true,
          alertError: false,
          errorMessage: '',
        },
        () => {
          if (this.props?.update) {
            this.props.update(user);
          }
          //this.props.update causes handleChange to fire, which resets alertSuccess
          this.setState({
            alertSuccess: true,
          });
        },
      );
    }
  };

  nativeCurrencyCode() {
    const region = libRegions.getRegionByCode(this.state.formData.country_code);
    return region ? region.currencyCode : 'Currency is not available as country is not set';
  }

  render() {
    const alertError = (
      <Alert severity="error">
        <strong>Error!</strong> Unable to save user. {this.state.errorMessage}.
      </Alert>
    );

    const alertSuccess = (
      <Alert severity="success">
        <strong>Success!</strong> User Information Saved
      </Alert>
    );

    const partnershipsAlertError = (
      <Alert severity="error">
        <strong>Error!</strong> Unable to save partnership details. {this.state.partnershipsErrorMessage}.
      </Alert>
    );

    const { schema, formData, uiSchema } = this.handleStateData();

    return (
      <Box sx={{ mt: 4 }}>
        {this.state.alertError && alertError}
        {this.state.alertSuccess && alertSuccess}
        {this.state.partnershipsAlertError && partnershipsAlertError}
        <Form
          formData={formData}
          schema={schema}
          uiSchema={uiSchema}
          templates={FORM_TEMPLATES}
          onSubmit={this.handleSubmit}
          onChange={this.handleChange}
          validator={validator}
          customValidate={this.customValidate}
        >
          <dl>
            <dt className="h6 text-muted text-uppercase">Native Currency Code</dt>
            <dd>{this.nativeCurrencyCode()}</dd>
          </dl>

          <button
            ref={(btn) => {
              this.submitButton = btn;
            }}
            className="hidden"
          />
        </Form>
        {this.state.alertError && alertError}
        {this.state.alertSuccess && alertSuccess}
        {this.state.partnershipsAlertError && partnershipsAlertError}
      </Box>
    );
  }
}
